import { FC } from 'react';

import {
  LoginSlideKey,
  LoginSlideVisualProps,
} from './types';

type BoardItem = {
  titleKey: string;
  for: number;
  against: number;
  status: 'approved' | 'open';
};

export function OwnershipVisual({ t, dark }: LoginSlideVisualProps) {
  const segs = [
    { v: 32, c: '#6576ff' },
    { v: 25, c: '#798bff' },
    { v: 16, c: '#7de1f8' },
    { v: 14, c: '#ffa9ce' },
    { v: 8, c: '#b8acff' },
    { v: 5, c: '#c4cefe' },
  ];
  let acc = 0;
  const stops = segs
    .map((s) => {
      const f = acc;
      acc += s.v;
      return `${s.c} ${f}% ${acc}%`;
    })
    .join(',');

  const centerBg = dark ? '#111729' : '#fff';
  const centerTxt = dark ? '#fff' : '#111729';
  const centerSub = dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.38)';
  const labelClr = dark ? 'rgba(255,255,255,0.7)' : '#4a5568';
  const valClr = dark ? '#fff' : '#111729';

  const rows: [string, string, string][] = [
    ['LoginCaptableFounders', '32%', '#6576ff'],
    ['LoginCaptableInvestors', '41%', '#798bff'],
    ['LoginCaptableEsop', '14%', '#ffa9ce'],
    ['LoginCaptableOther', '13%', '#b8acff'],
  ];

  return (
    <div
      style={{
        display: 'flex',
        gap: 20,
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: 140,
          height: 140,
          flex: '0 0 auto',
        }}
      >
        <div
          style={{
            width: 140,
            height: 140,
            borderRadius: '50%',
            background: `conic-gradient(${stops})`,
            boxShadow: '0 0 32px rgba(101,118,255,0.3)',
          }}
        />
        <div
          style={{
            position: 'absolute',
            inset: 28,
            borderRadius: '50%',
            background: centerBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            boxShadow: dark ? 'none' : '0 0 0 1px rgba(0,0,0,0.06)',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--sl-font-mono)',
              fontWeight: 700,
              fontSize: 22,
              color: centerTxt,
              lineHeight: 1,
            }}
          >
            100%
          </div>
          <div style={{ fontSize: 9, color: centerSub, marginTop: 3 }}>
            {t('LoginCaptableEquity')}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {rows.map(([labelKey, value, color]) => (
          <div
            key={labelKey}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 12,
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: 3,
                background: color,
                flex: '0 0 auto',
              }}
            />
            <span style={{ color: labelClr, width: 68 }}>{t(labelKey)}</span>
            <span
              style={{
                fontFamily: 'var(--sl-font-mono)',
                fontWeight: 600,
                color: valClr,
              }}
            >
              {value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PlansVisual({ t, dark }: LoginSlideVisualProps) {
  const plans = [
    { nameKey: 'LoginPlansEsop2022', vested: 64, color: '#7de1f8' },
    { nameKey: 'LoginPlansPhantom2023', vested: 38, color: '#6576ff' },
    { nameKey: 'LoginPlansAdvisors', vested: 80, color: '#ffa9ce' },
  ];
  const labelClr = dark ? 'rgba(255,255,255,0.8)' : '#2d3748';
  const trackBg = dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)';
  const cardBg = dark ? 'rgba(255,255,255,0.08)' : '#fff';
  const cardBdr = dark ? 'none' : '1px solid rgba(0,0,0,0.07)';
  const valClr = dark ? '#fff' : '#111729';
  const subClr = dark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.42)';
  const stats: [string, string][] = [
    ['9', 'LoginPlansBeneficiaries'],
    ['372K', 'LoginPlansUnitsGranted'],
    ['64%', 'LoginPlansAvgVested'],
  ];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        width: '100%',
        maxWidth: 340,
      }}
    >
      {plans.map((p) => (
        <div key={p.nameKey}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 7,
              fontSize: 12,
            }}
          >
            <span style={{ color: labelClr, fontWeight: 500 }}>
              {t(p.nameKey)}
            </span>
            <span
              style={{
                fontFamily: 'var(--sl-font-mono)',
                color: p.color,
                fontWeight: 600,
              }}
            >
              {t('LoginPlansVested', { percent: p.vested })}
            </span>
          </div>
          <div
            style={{
              height: 10,
              borderRadius: 5,
              background: trackBg,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${p.vested}%`,
                height: '100%',
                background: p.color,
                borderRadius: 5,
                boxShadow: `0 0 10px ${p.color}88`,
              }}
            />
          </div>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
        {stats.map(([v, labelKey]) => (
          <div
            key={labelKey}
            style={{
              flex: 1,
              background: cardBg,
              border: cardBdr,
              borderRadius: 10,
              padding: '10px 12px',
              textAlign: 'center',
              boxShadow: dark ? 'none' : '0 1px 3px rgba(0,0,0,0.06)',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--sl-font-mono)',
                fontWeight: 700,
                fontSize: 18,
                color: valClr,
              }}
            >
              {v}
            </div>
            <div style={{ fontSize: 10, color: subClr, marginTop: 2 }}>
              {t(labelKey)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function BoardsVisual({ t, dark }: LoginSlideVisualProps) {
  const items: BoardItem[] = [
    {
      titleKey: 'LoginBoardsItemAnnualAccounts',
      for: 86,
      against: 6,
      status: 'approved',
    },
    {
      titleKey: 'LoginBoardsItemSeriesB',
      for: 0,
      against: 0,
      status: 'open',
    },
    {
      titleKey: 'LoginBoardsItemBoardReelection',
      for: 74,
      against: 18,
      status: 'approved',
    },
  ];
  const cardBg = dark ? 'rgba(255,255,255,0.07)' : '#fff';
  const cardBdr = dark
    ? '1px solid rgba(255,255,255,0.1)'
    : '1px solid rgba(0,0,0,0.08)';
  const titClr = dark ? 'rgba(255,255,255,0.85)' : '#2d3748';
  const trackBg = dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.07)';

  const getStatusBadgeColor = (status: BoardItem['status']) => {
    if (status === 'approved') {
      return dark ? '#7de1f8' : '#0a8a6a';
    }
    return dark ? '#9cabff' : '#4a5eff';
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        width: '100%',
        maxWidth: 360,
      }}
    >
      {items.map((item) => (
        <div
          key={item.titleKey}
          style={{
            background: cardBg,
            border: cardBdr,
            borderRadius: 12,
            padding: '12px 14px',
            boxShadow: dark ? 'none' : '0 1px 4px rgba(0,0,0,0.05)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 8,
              gap: 10,
            }}
          >
            <span
              style={{
                fontSize: 12,
                color: titClr,
                fontWeight: 500,
                lineHeight: 1.3,
              }}
            >
              {t(item.titleKey)}
            </span>
            <span
              style={{
                fontSize: 10,
                padding: '2px 8px',
                borderRadius: 20,
                fontWeight: 600,
                whiteSpace: 'nowrap',
                background:
                  item.status === 'approved'
                    ? 'rgba(30,224,172,0.15)'
                    : 'rgba(101,118,255,0.12)',
                color: getStatusBadgeColor(item.status),
              }}
            >
              {t(
                item.status === 'approved'
                  ? 'LoginBoardsStatusApproved'
                  : 'LoginBoardsStatusOpen'
              )}
            </span>
          </div>
          {item.status !== 'open' && (
            <div
              style={{
                height: 6,
                borderRadius: 4,
                overflow: 'hidden',
                display: 'flex',
                gap: 2,
              }}
            >
              <div style={{ width: `${item.for}%`, background: '#1ee0ac' }} />
              <div
                style={{ width: `${item.against}%`, background: '#e85347' }}
              />
              <div style={{ flex: 1, background: trackBg }} />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export const LOGIN_SLIDER_VISUALS: Record<
  LoginSlideKey,
  FC<LoginSlideVisualProps>
> = {
  captable: OwnershipVisual,
  plans: PlansVisual,
  boards: BoardsVisual,
};
